import { computed, effect, Injectable, Signal, signal } from '@angular/core';
import { Polyline } from 'leaflet';
import { SpanProperties } from '../../../../span-progress-entities';
import { HighlightedFeatureInfo } from '../../../../span-progress-view/map-interaction/map-interaction.service';
import { calculateAndFormatSpanLength } from '../../../../utils/span-length.helper';

@Injectable({
  providedIn: 'root',
})
export class SelectSpanModeService {
  private readonly _isSelectSpanMode = signal<boolean>(false);
  readonly isSelectSpanMode = this._isSelectSpanMode.asReadonly();

  private readonly _isEditMode = signal<boolean>(false);
  readonly isEditMode = this._isEditMode.asReadonly();

  private readonly _selectedSpanLength = signal<string>('0');
  readonly selectedSpanLength = this._selectedSpanLength.asReadonly();

  private readonly _selectedSpanLayers = signal<Polyline[]>([]);
  readonly selectedSpanLayers = this._selectedSpanLayers.asReadonly();

  readonly selectedSpans: Signal<SpanProperties[]> = computed(() =>
    this._selectedSpanLayers().map(
      (layer) => layer.feature?.properties as SpanProperties,
    ),
  );
  readonly selectedSpanIds: Signal<string[]> = computed(() =>
    this._selectedSpanLayers().map(
      (layer) => (layer.feature?.properties as SpanProperties).spanId,
    ),
  );

  private readonly _originalReportSpanIds = signal<string[]>([]);
  readonly originalReportSpanIds = this._originalReportSpanIds.asReadonly();

  private readonly _originalHighlightedLayers = signal<
    HighlightedFeatureInfo[]
  >([]);
  readonly originalHighlightedLayers =
    this._originalHighlightedLayers.asReadonly();

  private readonly _isMultiSpanHelpModalOpen = signal(false);
  readonly isMultiSpanHelpModalOpen =
    this._isMultiSpanHelpModalOpen.asReadonly();

  readonly displayWarningMaxSpanLength: Signal<boolean> = computed(
    () => this._selectedSpanLayers().length > 1,
  );

  constructor() {
    this.updateSelectedSpanLengthEffect();
  }

  setSelectSpanMode = (value: boolean) => {
    this._isSelectSpanMode.set(value);
  };

  setIsEditMode = (value: boolean) => {
    this._isEditMode.set(value);
  };

  setSelectSpanLength = (lengths: string[]) => {
    this._selectedSpanLength.set(calculateAndFormatSpanLength(lengths));
  };

  addSelectedSpanLayer = (spanLayer: Polyline) => {
    this._selectedSpanLayers.update((currentlySelectedSpanLayers) => [
      ...currentlySelectedSpanLayers,
      spanLayer,
    ]);
  };

  removeSelectedSpanLayer = (spanLayer: Polyline) => {
    this._selectedSpanLayers.update((currentlySelectedSpanLayers) =>
      currentlySelectedSpanLayers.filter(
        (selectedSpanLayer) => selectedSpanLayer !== spanLayer,
      ),
    );
  };

  clearSelectedSpanLayers = () => {
    this._selectedSpanLayers.set([]);
    this._originalReportSpanIds.set([]);
    this._originalHighlightedLayers.set([]);
  };

  setOriginalReportSpanIds = (ids: string[]): void => {
    this._originalReportSpanIds.set(ids);
  };

  setOriginalHighlightedLayers = (layers: HighlightedFeatureInfo[]): void => {
    this._originalHighlightedLayers.set(layers);
  };

  private updateSelectedSpanLengthEffect = () => {
    effect(() => {
      this._selectedSpanLength.set(
        calculateAndFormatSpanLength(
          this.selectedSpans().map((spanProperties) => spanProperties.length),
        ),
      );
    });
  };

  setIsMultiSpanHelpModalOpen = (state: boolean) => {
    this._isMultiSpanHelpModalOpen.set(state);
  };
}
